import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { RgbColorPicker } from 'react-colorful';
import ReactQuill from 'react-quill';

import {
  FormHelperText,
  Popover,
  Stack,
  TextFieldProps,
  Tooltip
} from '@mui/material';

import {
  formats,
  injectFontColorIntoHtml,
  rgbObjectToString,
  rgbStringToObject
} from '../../utils/colorUtils';
import { isRichTextEmpty } from '../../utils/richTextUtils';

import 'react-quill/dist/quill.snow.css';
import './style.scss';

const BLACK_COLOR = 'rgb(0, 0, 0)';
const QUILL_FORMAT_DELAY = 50;

export type AcAdvancedTextInputProps = TextFieldProps & {
  header?: string;
  tooltip?: string;
  value?: string;
  textarea?: boolean;
  helperText?: string;
  enableCopy?: boolean;
  required?: boolean;
  customClass?: string;
  enableColorPicker?: boolean;
  setValue: (value: string) => any;
  fontColor?: string;
  handleOnEmpty?: () => void;
  onUserContentChange?: (html: string) => void;
  placeholder?: string;
};

export const AcAdvancedTextInput: React.FC<AcAdvancedTextInputProps> = ({
  header = '',
  tooltip = '',
  textarea = false,
  value = '',
  helperText = '',
  required = false,
  customClass = '',
  enableColorPicker = false,
  setValue,
  fontColor,
  handleOnEmpty,
  onUserContentChange,
  placeholder = 'Start typing your content...',
  ...props
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [colorPickerAnchor, setColorPickerAnchor] =
    useState<HTMLElement | null>(null);
  const [selectedColor, setSelectedColor] = useState(BLACK_COLOR);
  const [userSelectedColor, setUserSelectedColor] = useState<string | null>(null);

  const supportsCustomFontColor = enableColorPicker || fontColor;
  const valueWithColor =
    supportsCustomFontColor && value
      ? injectFontColorIntoHtml(value, fontColor)
      : value;

  const handleTextColor = useCallback(() => {
    const toolbar = quillRef.current?.getEditor()?.getModule('toolbar');
    const textColorButton = toolbar?.container?.querySelector('.ql-textColor');
    if (textColorButton) {
      setColorPickerAnchor(textColorButton as HTMLElement);
    }
  }, []);

  const handleColorChange = useCallback(
    (color: { r: number; g: number; b: number }) => {
      const rgbString = rgbObjectToString(color);
      setSelectedColor(rgbString);
    },
    []
  );

  const handleCloseColorPicker = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      quill.formatText(0, quill.getLength() - 1, 'color', selectedColor);
      setUserSelectedColor(selectedColor);
      const html = quill.root.innerHTML;
      setValue(html);
      onUserContentChange?.(html);
    }
    setColorPickerAnchor(null);
  }, [selectedColor, setValue, onUserContentChange]);

  const handleQuillChange = useCallback(
    (content: string, _delta: unknown, source: string) => {
      if (source !== 'user') return;
      setValue(content);
      onUserContentChange?.(content);
    },
    [setValue, onUserContentChange]
  );

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline'],
          ['link'],
          ...(enableColorPicker ? [['textColor']] : [])
        ],
        handlers: {
          ...(enableColorPicker ? { textColor: handleTextColor } : {})
        }
      }
    }),
    [enableColorPicker, handleTextColor]
  );

  const applyColorToEditor = useCallback((color: string) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    setTimeout(() => {
      const len = quill.getLength();
      if (len > 1) quill.formatText(0, len - 1, 'color', color);
    }, QUILL_FORMAT_DELAY);
  }, []);

  useEffect(() => {
    if (!supportsCustomFontColor) return;

    const isEmpty = isRichTextEmpty(value);

    if (isEmpty) {
      setSelectedColor(BLACK_COLOR);
      setUserSelectedColor(null);
      handleOnEmpty?.();
      return;
    }

    const colorToApply = userSelectedColor ?? fontColor ?? BLACK_COLOR;
    setSelectedColor(colorToApply);
    applyColorToEditor(colorToApply);
  }, [
    supportsCustomFontColor,
    fontColor,
    value,
    userSelectedColor,
    applyColorToEditor,
    handleOnEmpty
  ]);

  return (
    <Stack
      className={`input-container formContent-input ${customClass}`}
      direction="column"
    >
      <Stack
        direction="row"
        alignItems="center"
        marginBottom="8px"
        gap="5px"
        className="input-headerBlock formContent-input-fieldTitle"
      >
        {header && (
          <Tooltip arrow title={tooltip} placement="top">
            <h3>
              {required && (
                <span style={{ color: 'red', marginRight: '6px' }}>*</span>
              )}
              {header}
            </h3>
          </Tooltip>
        )}
      </Stack>
      <div className="rich-text-editor-wrapper">
        <div data-text-editor="name">
          <ReactQuill
            key={fontColor ?? 'default'}
            className={`rich-text-editor ${customClass}`}
            ref={quillRef}
            theme="snow"
            modules={modules}
            formats={formats}
            value={valueWithColor}
            onChange={handleQuillChange}
            placeholder={placeholder}
            bounds={`[data-text-editor="name"]`}
          />
        </div>
        <Popover
          open={Boolean(colorPickerAnchor)}
          anchorEl={colorPickerAnchor}
          onClose={handleCloseColorPicker}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{
            paper: {
              style: {
                padding: '16px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 9999,
                maxWidth: '220px',
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                opacity: 1
              }
            }
          }}
        >
          <div className="w-[180px]">
            <RgbColorPicker
              color={rgbStringToObject(selectedColor)}
              onChange={handleColorChange}
              className="w-full h-[150px]"
            />
            <div style={{ marginTop: '12px' }}>
              <input
                type="text"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono text-center"
                placeholder={BLACK_COLOR}
              />
            </div>
          </div>
        </Popover>
      </div>
      {helperText && (
        <FormHelperText
          error={props.error}
          sx={{
            marginTop: '8px',
            fontSize: '12px',
            color: props.error ? '#d32f2f' : '#666'
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </Stack>
  );
};
